import React from 'react';
import { motion } from 'framer-motion';
import { Upload, Wand2, Rocket, Settings } from 'lucide-react';
import Section from './Section';

const steps = [
  {
    number: '1',
    icon: Upload,
    title: 'Upload Your Content',
    description: 'Drop in any content — blog posts, product updates, transcripts, or rough notes.',
    color: 'from-indigo-500 to-indigo-600',
  },
  {
    number: '2',
    icon: Wand2,
    title: 'AI Transforms It',
    description: 'Our AI adapts your content for each platform while maintaining your brand voice.',
    color: 'from-violet-500 to-violet-600',
  },
  {
    number: '3',
    icon: Rocket,
    title: 'Review & Deploy',
    description: 'Preview your content, tweak if needed, and schedule to all platforms with one click.',
    color: 'from-pink-500 to-pink-600',
  },
];

const HowItWorks = () => {
  return (
    <Section id="how-it-works" background="default" data-testid="how-it-works-section">
      {/* Header */}
      <div className="text-center mb-10">
        <motion.span 
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-50 rounded-full text-sm font-medium text-indigo-600 mb-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Settings className="w-4 h-4" />
          How It Works
        </motion.span>
        <motion.h2 
          className="text-3xl md:text-4xl font-bold text-slate-800 mb-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          Three Steps to Content Freedom
        </motion.h2>
        <motion.p 
          className="text-slate-500 text-lg max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          From upload to ready-to-publish<br className="md:hidden" />{' '}in minutes.
        </motion.p>
      </div>

      {/* Steps */}
      <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
        {steps.map((step, index) => (
          <motion.div
            key={step.number}
            className="relative bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-lg transition-shadow duration-300 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
          >
            {/* Number Badge */}
            <div className="absolute -top-3 -right-3 w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-sm font-bold text-slate-500 border-2 border-white shadow">
              {step.number}
            </div>

            {/* Icon */}
            <div className={`w-16 h-16 bg-gradient-to-br ${step.color} rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg`}>
              <step.icon className="w-8 h-8 text-white" />
            </div>

            {/* Content */}
            <h3 className="text-lg font-bold text-slate-800 mb-2">{step.title}</h3>
            <p className="text-sm text-slate-500 leading-relaxed">{step.description}</p>
          </motion.div>
        ))}
      </div>
    </Section>
  );
};

export default HowItWorks;
